import { useCallback } from 'react';
import Cookies from 'js-cookie';
import { AuthorizationAPI } from '../apis/AuthorizationAPI';
import { getFromCache, saveToCache } from '../utils/indexedDBCache';
import { PROFILE_PICTURE_CACHE_KEY } from '../constants';
// Module-level cache and request tracking
let cachedProfilePicture = undefined;
let ongoingRequest = null;
/**
 * Hook to get profile picture with module + IndexedDB caching.
 * Caching strategy: module cache -> IndexedDB -> ongoing request -> API
 */
export const useProfilePictureData = () => {
    const getProfilePicture = useCallback(async () => {
        // 1. Return module-level cache if available
        if (cachedProfilePicture !== undefined) {
            return cachedProfilePicture;
        }
        // 2. Check if user is authenticated
        const accessToken = Cookies.get('access_token');
        if (!accessToken) {
            return null;
        }
        // 3. Check IndexedDB cache
        try {
            const cachedFromDB = await getFromCache(PROFILE_PICTURE_CACHE_KEY);
            if (cachedFromDB) {
                cachedProfilePicture = cachedFromDB.value;
                return cachedFromDB.value;
            }
        }
        catch (error) {
            console.warn('Failed to retrieve profile picture from IndexedDB:', error);
        }
        // 4. Return ongoing request (prevent duplicate API calls)
        if (ongoingRequest) {
            return ongoingRequest;
        }
        // 5. Fetch from API
        const requestPromise = (async () => {
            try {
                const response = await AuthorizationAPI.getUserProfilePicture();
                const resolvedProfilePicture = response?.hasProfilePicture && response?.profilePicture
                    ? response.profilePicture
                    : null;
                cachedProfilePicture = resolvedProfilePicture;
                try {
                    await saveToCache(PROFILE_PICTURE_CACHE_KEY, { value: resolvedProfilePicture });
                }
                catch (error) {
                    console.warn('Failed to cache profile picture to IndexedDB:', error);
                }
                return resolvedProfilePicture;
            }
            catch {
                cachedProfilePicture = null;
                return null;
            }
            finally {
                ongoingRequest = null;
            }
        })();
        ongoingRequest = requestPromise;
        return requestPromise;
    }, []);
    return getProfilePicture;
};
/**
 * Clear the module-level cache (useful for logout)
 */
export const clearProfilePictureCache = () => {
    cachedProfilePicture = undefined;
    ongoingRequest = null;
};
